import React, { useEffect, useMemo, useState } from "react";
import * as THREE from "three";
import { Edges } from "@react-three/drei";
import { evaluateExpression } from "../../engine/evaluateBindings";

function resolvePoint(pt, params) {
  return {
    x: evaluateExpression(pt?.x ?? 0, params, 0),
    y: evaluateExpression(pt?.y ?? 0, params, 0),
  };
}

function buildPath(points = [], params = {}) {
  if (!Array.isArray(points) || points.length < 3) return null;

  const resolved = points.map((pt) => resolvePoint(pt, params));
  const path = new THREE.Path();

  path.moveTo(resolved[0].x, resolved[0].y);

  for (let i = 1; i < resolved.length; i++) {
    path.lineTo(resolved[i].x, resolved[i].y);
  }

  path.lineTo(resolved[0].x, resolved[0].y);
  return path;
}

function buildShapeFromProfile(profile, params) {
  if (!profile) return null;

  if (profile.type === "rect") {
    const width = evaluateExpression(profile.width_expr ?? "width", params, 100);
    const height = evaluateExpression(profile.height_expr ?? "height", params, 100);

    const shape = new THREE.Shape();
    shape.moveTo(0, 0);
    shape.lineTo(width, 0);
    shape.lineTo(width, height);
    shape.lineTo(0, height);
    shape.lineTo(0, 0);

    return shape;
  }

  if (
    profile.type === "polygon" &&
    Array.isArray(profile.points) &&
    profile.points.length >= 3
  ) {
    const outline = buildPath(profile.points, params);
    if (!outline) return null;

    const shape = new THREE.Shape(outline.getPoints());

    if (Array.isArray(profile.cutouts)) {
      profile.cutouts.forEach((cutout) => {
        if (
          cutout?.type === "polygon" &&
          Array.isArray(cutout.points) &&
          cutout.points.length >= 3
        ) {
          const hole = buildPath(cutout.points, params);
          if (hole) shape.holes.push(hole);
        }
      });
    }

    return shape;
  }

  return null;
}

function createBoardMaterials({
  hovered = false,
  opacity = 1,
  wireframe = false,
  color = "#2563eb",
}) {
  const common = {
    roughness: 0.65,
    metalness: 0,
    transparent: opacity < 1 || wireframe,
    opacity: wireframe ? 0.08 : opacity,
    depthWrite: !wireframe && opacity >= 1,
    depthTest: true,
    side: THREE.DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: 1,
    polygonOffsetUnits: 1,
  };

  const edge = new THREE.MeshStandardMaterial({
    ...common,
    color: hovered ? "#60a5fa" : color,
  });

  const front = new THREE.MeshStandardMaterial({
    ...common,
    color,
  });

  const back = new THREE.MeshStandardMaterial({
    ...common,
    color,
  });

  return [edge, front, back, edge, edge, edge, edge];
}

function assignFrontBackMaterials(geometry) {
  const pos = geometry.attributes.position;
  if (!pos) return;

  geometry.clearGroups();

  const normal = new THREE.Vector3();
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();

  const index = geometry.index;

  if (!index) {
    const faceCount = pos.count / 3;

    for (let i = 0; i < faceCount; i++) {
      a.fromBufferAttribute(pos, i * 3);
      b.fromBufferAttribute(pos, i * 3 + 1);
      c.fromBufferAttribute(pos, i * 3 + 2);

      normal.subVectors(c, b).cross(new THREE.Vector3().subVectors(a, b)).normalize();

      let materialIndex = 0;
      if (normal.z > 0.7) materialIndex = 1;
      if (normal.z < -0.7) materialIndex = 2;

      geometry.addGroup(i * 3, 3, materialIndex);
    }

    return;
  }

  const faceCount = index.count / 3;

  for (let i = 0; i < faceCount; i++) {
    const ia = index.getX(i * 3);
    const ib = index.getX(i * 3 + 1);
    const ic = index.getX(i * 3 + 2);

    a.fromBufferAttribute(pos, ia);
    b.fromBufferAttribute(pos, ib);
    c.fromBufferAttribute(pos, ic);

    normal.subVectors(c, b).cross(new THREE.Vector3().subVectors(a, b)).normalize();

    let materialIndex = 0;
    if (normal.z > 0.7) materialIndex = 1;
    if (normal.z < -0.7) materialIndex = 2;

    geometry.addGroup(i * 3, 3, materialIndex);
  }
}

function BoardEdgeMarkers({ width, height, depth }) {
  const z = depth / 2 + 0.8;

  return (
    <group>
      {/* bottom edge */}
      <mesh position={[0, -height / 2, z]}>
        <boxGeometry args={[width, 6, 6]} />
        <meshStandardMaterial color="#a855f7" />
      </mesh>

      {/* top edge */}
      <mesh position={[0, height / 2, z]}>
        <boxGeometry args={[width, 6, 6]} />
        <meshStandardMaterial color="#eab308" />
      </mesh>

      {/* left edge */}
      <mesh position={[-width / 2, 0, z]}>
        <boxGeometry args={[6, height, 6]} />
        <meshStandardMaterial color="#ef4444" />
      </mesh>

      {/* right edge */}
      <mesh position={[width / 2, 0, z]}>
        <boxGeometry args={[6, height, 6]} />
        <meshStandardMaterial color="#22c55e" />
      </mesh>
    </group>
  );
}

function toTextureUrl(url) {
  if (!url) return null;

  const str = String(url);

  const absoluteUrl = str.startsWith("http")
    ? str
    : `https://modularobjects.com/mailserver${str}`;

  return `https://modularobjects.com/mailserver/api/interior/texture-proxy?url=${encodeURIComponent(
    absoluteUrl
  )}`;
}

function getPrimaryColorTexture(material) {
  return (material?.textures || []).find(
    (t) =>
      String(t.texture_role || "") === "color" &&
      (Number(t.is_primary) === 1 || t.is_primary === true)
  );
}

export default function ProfileExtrudeMesh({
  profile,
  parameters = {},
  thicknessExpr = "thickness",
  color = "#d1d5db",
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  selected = false,
  hovered = false,
  opacity = 1,
  onClick,
  onDoubleClick,
  viewMode = "full",
  wireframe = false,
  materialAssignments = {},
  materials = [],
  uvOrigin = { x: 0, y: 0, z: 0 },
  nodeId,
}) {

function buildEdgeMaterial(edgeSlot = {}, materialPropsArg = {}) {
  const hasIndividualMaterial = Boolean(edgeSlot?.materialId);

  const mat =
    materials.find((m) => String(m.id) === String(edgeSlot?.materialId)) ||
    edgeMaterial;

  const individualTexture = hasIndividualMaterial
    ? edgeTextureMap[edgeSlot.materialId]
    : null;

  const map = individualTexture || edgeColorTexture || null;

  return new THREE.MeshStandardMaterial({
    color: map ? "#ffffff" : mat?.base_color || "#cccccc",
    map,
    roughness: 0.4,
    metalness: 0,
    side: THREE.DoubleSide,
    ...materialPropsArg,
  });
}

function assignMaterialGroups(geometry) {
  const pos = geometry.attributes.position;
  const index = geometry.index;

  geometry.clearGroups();

  const normal = new THREE.Vector3();
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();

  const faceCount = index ? index.count / 3 : pos.count / 3;

  for (let i = 0; i < faceCount; i++) {
    const ia = index ? index.getX(i * 3) : i * 3;
    const ib = index ? index.getX(i * 3 + 1) : i * 3 + 1;
    const ic = index ? index.getX(i * 3 + 2) : i * 3 + 2;

    a.fromBufferAttribute(pos, ia);
    b.fromBufferAttribute(pos, ib);
    c.fromBufferAttribute(pos, ic);

    normal.subVectors(c, b).cross(new THREE.Vector3().subVectors(a, b)).normalize();

    let materialIndex = 0;

    // FRONT / BACK
    if (normal.z > 0.7) materialIndex = 1;
    else if (normal.z < -0.7) materialIndex = 2;

    // EDGES
    else if (normal.y > 0.7) materialIndex = 3; // top
    else if (normal.y < -0.7) materialIndex = 4; // bottom
    else if (normal.x < -0.7) materialIndex = 5; // left
    else if (normal.x > 0.7) materialIndex = 6; // right

    geometry.addGroup(i * 3, 3, materialIndex);
  }
}

  
  
const width = evaluateExpression(profile?.width_expr ?? "width", parameters, 100);
const height = evaluateExpression(profile?.height_expr ?? "height", parameters, 100);
const depth = Math.max(0.1, evaluateExpression(thicknessExpr, parameters, 18));

const isXray = viewMode === "xray";
const isHiddenLine = viewMode === "hiddenLine";
const isWireframeMode = wireframe || viewMode === "wireframe";
const isRealistic = viewMode === "realistic";
const isTechnical = isHiddenLine || isWireframeMode || isXray;

const materialOpacity = Math.max(
  0.05,
  Math.min(1, Number(opacity ?? 1))
);

const isTransparentView = isWireframeMode || isHiddenLine;

const transparencyProps = {
  transparent: materialOpacity < 1 || isTransparentView,
  opacity: materialOpacity,
  depthWrite: !isTransparentView && materialOpacity >= 1,
  depthTest: true,
};

const bodyAssignment =
  materialAssignments?.body ||
  materialAssignments?.front ||
  {};

const edgeAssignment =
  materialAssignments?.edge ||
  bodyAssignment;

const edges = materialAssignments?.edges || {};

  const edgeThicknessMm = Number(edgeAssignment.edgeThicknessMm ?? 1);

const bodyMaterial = materials.find(
  (m) => String(m.id) === String(bodyAssignment.materialId)
);

const edgeMaterial = materials.find(
  (m) => String(m.id) === String(edgeAssignment.materialId)
);

const bodyTexture = getPrimaryColorTexture(bodyMaterial);
const edgeTexture = getPrimaryColorTexture(edgeMaterial);

const bodyTextureUrl = toTextureUrl(bodyTexture?.file_url);
const edgeTextureUrl = toTextureUrl(edgeTexture?.file_url);

const uvMode = bodyAssignment.uv_mode || "local";

const geometry = useMemo(() => {
  const shape = buildShapeFromProfile(profile, parameters);
  if (!shape) return null;

  const g = new THREE.ExtrudeGeometry(shape, {
    depth,
    bevelEnabled: false,
    steps: 1,
  });

  g.center();
  g.computeVertexNormals();
  g.computeBoundingBox();
  g.computeBoundingSphere();

// fizička dimenzija teksture u mm
const textureRealWidthMm = Number(bodyTexture?.real_width_mm || 2800);
const textureRealHeightMm = Number(bodyTexture?.real_height_mm || 2070);

const pos = g.attributes.position;
const uvs = [];

g.computeBoundingBox();
const box = g.boundingBox;

for (let i = 0; i < pos.count; i++) {
  const x = pos.getX(i);
  const y = pos.getY(i);

  const u =
  uvMode === "global"
    ? (x + Number(uvOrigin.x || 0)) / textureRealWidthMm
    : (x - box.min.x) / textureRealWidthMm;

const v =
  uvMode === "global"
    ? (y + Number(uvOrigin.y || 0)) / textureRealHeightMm
    : (y - box.min.y) / textureRealHeightMm;

uvs.push(u, v);
}

g.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));

  assignMaterialGroups(g);

  return g;
}, [
  profile,
  parameters,
  depth,
  bodyTexture?.real_width_mm,
  bodyTexture?.real_height_mm,
  uvMode,
  uvOrigin?.x,
  uvOrigin?.y,
]);

const [colorTexture, setColorTexture] = useState(null);
const [edgeColorTexture, setEdgeColorTexture] = useState(null);

const [edgeTextureMap, setEdgeTextureMap] = useState({});

useEffect(() => {
  if (!isRealistic || !edgeTextureUrl) {
    setEdgeColorTexture(null);
    return;
  }

  const loader = new THREE.TextureLoader();
  loader.setCrossOrigin("anonymous");

  loader.load(edgeTextureUrl, (tex) => {
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(1, 1);
  tex.anisotropy = 8;

  tex.rotation =
    ((Number(edgeTexture?.rotation_deg || 0) +
      Number(edgeAssignment.textureRotation ?? 90)) *
      Math.PI) /
    180;

  tex.center.set(0.5, 0.5);
  tex.needsUpdate = true;

  setEdgeColorTexture(tex);
});
}, [
  edgeTextureUrl,
  isRealistic,
  edgeTexture?.rotation_deg,
  edgeAssignment.textureRotation,
]);

useEffect(() => {
  if (!isRealistic) {
    setEdgeTextureMap({});
    return;
  }

  const edgeSlots = [edges.top, edges.bottom, edges.left, edges.right].filter(
    (x) => x?.materialId
  );

  if (!edgeSlots.length) {
    setEdgeTextureMap({});
    return;
  }

  const loader = new THREE.TextureLoader();
  loader.setCrossOrigin("anonymous");

  edgeSlots.forEach((slot) => {
    const mat = materials.find((m) => String(m.id) === String(slot.materialId));
    const texDef = getPrimaryColorTexture(mat);
    const url = toTextureUrl(texDef?.file_url);

    if (!url) return;

    loader.load(url, (tex) => {
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.wrapS = THREE.RepeatWrapping;
      tex.wrapT = THREE.RepeatWrapping;
      tex.repeat.set(1, 1);
      tex.anisotropy = 8;

      tex.rotation =
        ((Number(texDef?.rotation_deg || 0) +
          Number(slot.textureRotation ?? 90)) *
          Math.PI) /
        180;

      tex.center.set(0.5, 0.5);
      tex.needsUpdate = true;

      setEdgeTextureMap((prev) => ({
        ...prev,
        [slot.materialId]: tex,
      }));
    });
  });
}, [isRealistic, JSON.stringify(edges), materials]);

useEffect(() => {
  if (!isRealistic || !bodyTextureUrl) {
    setColorTexture(null);
    return;
  }

  const loader = new THREE.TextureLoader();
  loader.setCrossOrigin("anonymous");

  loader.load(
    bodyTextureUrl,
    (tex) => {



tex.colorSpace = THREE.SRGBColorSpace;
tex.wrapS = THREE.MirroredRepeatWrapping;
tex.wrapT = THREE.MirroredRepeatWrapping;
tex.needsUpdate = true;

      function toThreeWrap(value) {
  if (value === "clamp") return THREE.ClampToEdgeWrapping;
  if (value === "mirror") return THREE.MirroredRepeatWrapping;
  return THREE.RepeatWrapping;
}

tex.wrapS = toThreeWrap(bodyTexture?.wrap_s);
tex.wrapT = toThreeWrap(bodyTexture?.wrap_t);

tex.repeat.set(1, 1);
tex.anisotropy = 8;

      tex.offset.set(
        Number(bodyTexture?.offset_x || 0),
        Number(bodyTexture?.offset_y || 0)
      );

      tex.rotation =
        ((Number(bodyTexture?.rotation_deg || 0) +
          Number(bodyAssignment.textureRotation || 0)) *
          Math.PI) /
        180;

      tex.colorSpace = THREE.SRGBColorSpace;
      tex.needsUpdate = true;

      setColorTexture(tex);
    },
    undefined,
   (err) => {
  console.error("Texture load failed:", bodyTextureUrl, err);
  setColorTexture(null);
}
  );
}, [
  isRealistic,
  bodyTextureUrl,
  bodyTexture?.scale_x,
  bodyTexture?.scale_y,
  bodyTexture?.offset_x,
  bodyTexture?.offset_y,
  bodyTexture?.rotation_deg,
  bodyTexture?.wrap_s,
  bodyTexture?.wrap_t,
  bodyAssignment.textureRotation,
]);

const materialProps = {
  ...transparencyProps,
  transparent: transparencyProps.transparent || isWireframeMode || isHiddenLine,
  opacity: isWireframeMode || isHiddenLine ? 0.08 : transparencyProps.opacity,
  depthWrite:
    isWireframeMode || isHiddenLine ? false : transparencyProps.depthWrite,
  depthTest: true,
  polygonOffset: true,
  polygonOffsetFactor: 1,
  polygonOffsetUnits: 1,
};

const meshMaterials = useMemo(() => {
  if (isHiddenLine) {
    const hiddenLineMaterial = new THREE.MeshStandardMaterial({
      color: "#ffffff",
      roughness: 0.65,
      metalness: 0,
      transparent: false,
      opacity: 1,
      depthWrite: true,
      side: THREE.DoubleSide,
      polygonOffset: true,
      polygonOffsetFactor: 1,
      polygonOffsetUnits: 1,
    });

    return [
      hiddenLineMaterial,
      hiddenLineMaterial,
      hiddenLineMaterial,
      hiddenLineMaterial,
      hiddenLineMaterial,
      hiddenLineMaterial,
      hiddenLineMaterial,
    ];
  }

  if (!isRealistic) {
    return createBoardMaterials({
  hovered,
  opacity: materialOpacity,
  wireframe,
  color,
});
  }

  return [
    // 0 EDGE / kant
    new THREE.MeshStandardMaterial({
      color: edgeColorTexture ? "#ffffff" : edgeMaterial?.base_color || "#cccccc",
      map: edgeColorTexture || null,
      roughness: 0.4,
      metalness: 0,
      side: THREE.DoubleSide,
      ...materialProps,
      
    }),

    // 1 FRONT
    new THREE.MeshStandardMaterial({
      color: colorTexture ? "#ffffff" : bodyMaterial?.base_color || "#cccccc",
      map: colorTexture || null,
      roughness: 0.35,
      metalness: 0,
      side: THREE.DoubleSide,
      ...materialProps,
    }),

    // 2 BACK
    new THREE.MeshStandardMaterial({
      color: colorTexture ? "#ffffff" : bodyMaterial?.base_color || "#cccccc",
      map: colorTexture || null,
      roughness: 0.35,
      metalness: 0,
      side: THREE.DoubleSide,
      ...materialProps,
    }),

    // 3 top edge
buildEdgeMaterial(edges.top, materialProps),

// 4 bottom edge
buildEdgeMaterial(edges.bottom, materialProps),

// 5 left edge
buildEdgeMaterial(edges.left, materialProps),

// 6 right edge
buildEdgeMaterial(edges.right, materialProps),
  ];
}, [
  hovered,
  opacity,
  materialOpacity,
  isRealistic,
  isHiddenLine,
  isWireframeMode,
  bodyMaterial,
  edgeMaterial,
  colorTexture,
  edgeColorTexture,
  edgeTextureMap,
  JSON.stringify(edges),
  wireframe,
]);


if (!geometry) return null;

return (
  <group position={position} rotation={rotation}>
    <mesh
      geometry={geometry}
      material={meshMaterials}
      userData={{ nodeId }}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      castShadow
      receiveShadow
    >
      {isHiddenLine ? (
  <Edges scale={1.001} color="#111827" threshold={1} />
) : isWireframeMode ? (
  <Edges scale={1.001} color="#334155" threshold={1} />
) : selected ? (
  <Edges scale={1.002} color="#1d4ed8" threshold={15} />
) : null}
    </mesh>

    {!isRealistic && !isWireframeMode && !isHiddenLine && (
  <BoardEdgeMarkers width={width} height={height} depth={depth} />
)}
  </group>
);
}